import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './RSVPForm.css';

function RSVPForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    attending: '',
    numberOfGuests: 1,
    guestNames: [],
    phone: '',
    message: ''
  });

  useEffect(() => {
    fetchInvite();
  }, [token]);

  const fetchInvite = async () => {
    try {
      const response = await axios.get(`/api/invites/${token}`);
      const inviteData = response.data;
      setInvite(inviteData);

      // Pre-populate form if guest has already responded
      if (inviteData.hasResponded && inviteData.rsvp) {
        const rsvp = inviteData.rsvp;
        setFormData({
          attending: rsvp.attending || '',
          numberOfGuests: rsvp.numberOfGuests || 1,
          guestNames: rsvp.guestNames ? rsvp.guestNames.split(', ').filter(name => name.trim()) : [],
          phone: rsvp.phone || '',
          message: rsvp.message || ''
        });
      }

      setLoading(false);
    } catch (err) {
      setError('Invalid or expired invitation link');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      // When numberOfGuests changes, adjust guestNames array
      if (name === 'numberOfGuests') {
        const numGuests = parseInt(value);
        const currentGuestNames = prev.guestNames || [];

        if (numGuests > 1) {
          // Need additional guest names (numberOfGuests - 1 because main guest is already known)
          const additionalGuestsNeeded = numGuests - 1;

          if (currentGuestNames.length < additionalGuestsNeeded) {
            // Add empty slots for new guests
            newData.guestNames = [
              ...currentGuestNames,
              ...Array(additionalGuestsNeeded - currentGuestNames.length).fill('')
            ];
          } else if (currentGuestNames.length > additionalGuestsNeeded) {
            // Remove extra slots
            newData.guestNames = currentGuestNames.slice(0, additionalGuestsNeeded);
          }
        } else {
          // Only 1 guest, no additional names needed
          newData.guestNames = [];
        }
      }

      return newData;
    });
  };

  const handleGuestNameChange = (index, value) => {
    setFormData(prev => {
      const newGuestNames = [...(prev.guestNames || [])];
      newGuestNames[index] = value;
      return {
        ...prev,
        guestNames: newGuestNames
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.attending) {
      setError('Please select whether you will be attending');
      return;
    }

    try {
      // Convert guestNames array to comma-separated string for backend
      const submitData = {
        ...formData,
        guestNames: Array.isArray(formData.guestNames)
          ? formData.guestNames.filter(name => name.trim()).join(', ')
          : formData.guestNames
      };

      await axios.post(`/api/rsvp/${token}`, submitData);
      navigate('/thank-you', {
        state: {
          token,
          attending: formData.attending
        }
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit RSVP. Please try again.');
    }
  };

  if (loading) {
    return <div className="loading">Loading invitation...</div>;
  }

  if (error && !invite) {
    return (
      <div className="container">
        <div className="card text-center">
          <h2>Oops!</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rsvp-page">
      <div className="container">
        <div className="card">
          <div className="rsvp-header">
            <h1>You're Invited!</h1>
            <h2 className="couple-names-small">Janelle & Justine</h2>
            <p className="wedding-details">April 14, 2026 • 12NN</p>
            <p className="venue-details">Cathedral Parish of Saint Paul the First Hermit, San Pablo City, Laguna</p>
          </div>

          <div className="divider"></div>

          <div className="invite-info">
            <p className="invited-guest">Dear <strong>{invite.guestName}</strong>,</p>
            <p className="invite-message">
              We would be honored to have you celebrate our special day with us.
              Please fill out the form below to let us know if you can make it!
            </p>
          </div>

          {invite.hasResponded && (
            <div className="already-responded">
              <p className="response-notice">✓ You've already responded to this invitation</p>
              {invite.attending === 'yes' && (
                <>
                  <p className="response-message">
                    We're excited to celebrate with you! Your personalized invitation is ready to view.
                  </p>
                  <Link to={`/invitation/${token}`} className="btn btn-primary view-invitation-btn">
                    View Your Invitation
                  </Link>
                </>
              )}
              <p className="update-note">You can update your response using the form below if needed.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="rsvp-form">
            <div className="form-group">
              <label htmlFor="primaryGuest">Primary Guest Name</label>
              <input
                type="text"
                id="primaryGuest"
                value={invite.guestName}
                disabled
                className="disabled-input"
              />
            </div>

            <div className="form-group">
              <label>Will you be attending? *</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="attending"
                    value="yes"
                    checked={formData.attending === 'yes'}
                    onChange={handleChange}
                  />
                  <span>Joyfully accepts</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="attending"
                    value="no"
                    checked={formData.attending === 'no'}
                    onChange={handleChange}
                  />
                  <span>Regretfully declines</span>
                </label>
              </div>
            </div>

            {formData.attending === 'yes' && (
              <>
                <div className="form-group">
                  <label htmlFor="numberOfGuests">Number of Guests (including yourself) *</label>
                  <p className="field-hint">Maximum allowed: {invite.maxGuests} {invite.maxGuests === 1 ? 'guest' : 'guests'}</p>
                  <input
                    type="number"
                    id="numberOfGuests"
                    name="numberOfGuests"
                    min="1"
                    max={invite.maxGuests}
                    value={formData.numberOfGuests}
                    onChange={handleChange}
                    required
                  />
                </div>

                {formData.numberOfGuests > 1 && (
                  <div className="form-group">
                    <label>Names of Additional Guests *</label>
                    <p className="field-hint">Please provide the name of each additional guest</p>
                    <div className="guest-names-list">
                      {Array.from({ length: formData.numberOfGuests - 1 }).map((_, index) => (
                        <input
                          key={index}
                          type="text"
                          placeholder={`Guest ${index + 2} Name`}
                          value={formData.guestNames[index] || ''}
                          onChange={(e) => handleGuestNameChange(index, e.target.value)}
                          required
                          className="guest-name-input"
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="+63 123 456 7890"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="message">Message to the Couple</label>
              <textarea
                id="message"
                name="message"
                placeholder="Share your well wishes..."
                value={formData.message}
                onChange={handleChange}
              />
            </div>

            {error && <p className="error-message">{error}</p>}

            <button type="submit" className="btn btn-primary btn-submit">
              Submit RSVP
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RSVPForm;
